import React, { useEffect, useState } from "react";
import { KeyRound, Save, ShieldCheck, User as UserIcon } from "lucide-react";
import AppLayout from "../../shared/components/AppLayout";
import Button from "../../shared/components/Button";
import accountService from "../../services/accountService";
import { useCurrentUser } from "../../hooks/useCurrentUser";

// Cognito pool policy: 8+ chars with upper, lower, number and symbol.
const PASSWORD_POLICY =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const Card: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  children: React.ReactNode;
}> = ({ title, description, icon: Icon, children }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    {children}
  </section>
);

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500";
const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";

const AccountSettingsPage: React.FC = () => {
  const { user } = useCurrentUser();

  // ── Profile ──────────────────────────────────────────────
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setPhone((user as any)?.profile?.phone_number || "");
  }, [user]);

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileBusy(true);
    setProfileMsg(null);
    setProfileErr(null);
    try {
      await accountService.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim(),
      });
      setProfileMsg("Profile updated.");
    } catch (err: any) {
      setProfileErr(
        err?.response?.data?.error || "Could not update your profile.",
      );
    } finally {
      setProfileBusy(false);
    }
  };

  // ── Password ─────────────────────────────────────────────
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    setPwErr(null);
    if (newPassword !== confirmPassword) {
      setPwErr("The new passwords do not match.");
      return;
    }
    // Cognito accepts a no-op change, which silently reports success and leaves
    // the password unchanged — misleading when someone means to rotate it.
    if (newPassword === oldPassword) {
      setPwErr("Your new password cannot be the same as your current password.");
      return;
    }
    if (!PASSWORD_POLICY.test(newPassword)) {
      setPwErr(
        "Password must be at least 8 characters and include upper- and lower-case letters, a number, and a symbol.",
      );
      return;
    }
    setPwBusy(true);
    try {
      await accountService.changePassword(oldPassword, newPassword);
      setPwMsg("Password changed.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setPwErr(typeof err === "string" ? err : "Could not change the password.");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <AppLayout active="account">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Account settings</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Manage your profile and password.
          </p>
        </div>

        <div className="space-y-5">
          {/* Profile */}
          <Card
            title="Profile"
            description="Your name and contact number as they appear in DocFi."
            icon={UserIcon}
          >
            {profileErr && (
              <div className="mb-4 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {profileErr}
              </div>
            )}
            {profileMsg && (
              <div className="mb-4 p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {profileMsg}
              </div>
            )}
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="first-name">
                    First name
                  </label>
                  <input
                    id="first-name"
                    className={inputClass}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="last-name">
                    Last name
                  </label>
                  <input
                    id="last-name"
                    className={inputClass}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass} htmlFor="phone">
                  Phone number
                </label>
                <input
                  id="phone"
                  className={inputClass}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 000 0000"
                />
              </div>
              {/* Email is the Cognito sign-in identity and is not editable here.
                  Shown read-only so the account is still identifiable. */}
              <div>
                <label className={labelClass} htmlFor="account-email">
                  Sign-in email
                </label>
                <input
                  id="account-email"
                  className={inputClass}
                  value={user?.email || ""}
                  disabled
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Contact an administrator if this needs to change.
                </p>
              </div>
              <div className="flex justify-end">
                <Button type="submit" icon={Save} loading={profileBusy}>
                  Save changes
                </Button>
              </div>
            </form>
          </Card>

          {/* Password */}
          <Card
            title="Password"
            description="Change the password you use to sign in."
            icon={KeyRound}
          >
            {pwErr && (
              <div className="mb-4 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {pwErr}
              </div>
            )}
            {pwMsg && (
              <div className="mb-4 p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {pwMsg}
              </div>
            )}
            <form onSubmit={savePassword} className="space-y-4">
              <div>
                <label className={labelClass} htmlFor="old-password">
                  Current password
                </label>
                <input
                  id="old-password"
                  type={showPw ? "text" : "password"}
                  className={inputClass}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="new-password">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type={showPw ? "text" : "password"}
                    className={inputClass}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="confirm-password">
                    Confirm new password
                  </label>
                  <input
                    id="confirm-password"
                    type={showPw ? "text" : "password"}
                    className={inputClass}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showPw}
                  onChange={() => setShowPw((v) => !v)}
                />
                Show passwords
              </label>
              <div className="flex justify-end">
                <Button type="submit" icon={ShieldCheck} loading={pwBusy}>
                  Change password
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default AccountSettingsPage;
